<template>
  <div v-bind:class="[large ? 'vue-wrapper' : 'skeleton-container', dashboarditem.loading || dashboarditem.updating ? 'active' : '']">
    <TheLoader v-if="large" :small="false" :active="dashboarditem.loading || dashboarditem.updating" :update="dashboarditem.updating"/>

    <table v-else class="clickable oddeven sortable skeleton-content">
      <thead>
        <tr>
          <th v-for="(thClass, thIndex) in skeletonThLines" v-bind:key="'sk-' + thIndex" v-bind:class="[thClass]"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(trLine, trIndex) in skeletonTdLines" v-bind:key="'sk-tr-' + trIndex">
          <td v-for="(tdClass, tdIndex) in trLine" v-bind:key="'sk-td-' + trIndex + '-' + tdIndex " v-bind:class="[tdClass]"></td>
        </tr>
      </tbody>
    </table>

    <section class="table-card-container">
      <table class="clickable visible oddeven sortable" :class="{'fixed': large}">
        <thead class="border">
          <tr>
            <th v-for="(heading, index) in dashboarditem.headings" v-bind:key="index" v-bind:class="[heading.class, heading.sortKey === dashboarditem.sort ? sortClassUnsigned : '']" @click="changeSorting(heading.sortKey)">
              {{heading.name}}
              <span id="sorttable_sortrevind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'desc'">&nbsp;</span>
              <span id="sorttable_sortfwdind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'asc'">&nbsp;</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in dashboarditem.items" v-bind:key="item.id" @click="openUnsoldItemModal(item)">
            <td class="nowrap medium">
              <a href="javascript:;" class="primary-title">{{item.objekt.Objektnr}} - {{item.info}}</a>
            </td>
            <td class="status-container">
              <span class="status-container" v-if="item.status === 'pending'">
                <span class="statusicon incomplete"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-warning"></use></svg></span>
                <span>Ubehandlet</span>
              </span>
              <span class="status-container" v-else-if="item.status === 'in-progress'">
                <span class="statusicon in-progress"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-play"></use></svg></span>
                <span>Under behandling</span>
              </span>
              <span class="status-container" v-else-if="item.status === 'on-hold'">
                <span class="statusicon on-hold"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-pause"></use></svg></span>
                <span>På vent</span>
              </span>
              <span class="status-container" v-else-if="item.status === 'resolved'">
                <span class="statusicon success"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-success"></use></svg></span>
                <span>Ferdigbehandlet</span>
              </span>
            </td>
            <td class="align-right">
              <span class="opacity-60">{{item.createdAt | avantidatetime}}</span>
            </td>
            <td class="align-right">
              <span v-if="item.bruker" class="avatar tiny name left" :class="strToAvatarClass(item.bruker.initials)">{{item.bruker.initials}} <span class="name">{{item.bruker.full_name || item.bruker.initials}}</span></span>
              <span v-else class="avatar tiny name left" :class="strToAvatarClass('-')">- <span class="name">Ingen ansvarlig</span></span>
            </td>
          </tr>
        </tbody>

        <template v-if="large">
          <tfoot v-if="dashboarditem.pages > 1">
            <tr>
              <td :colspan="dashboarditem.headings.length">
                <div class="pagination-container" style="margin-top: 0; padding: 1rem 0;">
                  <paginate v-model="dashboarditem.page"
                    :page-count="dashboarditem.pages"
                    :page-range="3"
                    :click-handler="setPage"
                    :container-class="'pagination'"
                    :prev-text="paginationArrow"
                    :next-text="paginationArrow"
                    :prev-class="'prev'"
                    :next-class="'next'"
                    >
                  </paginate>
                </div>
              </td>
            </tr>
          </tfoot>
        </template>
      </table>

      <div v-if="dashboarditem.items.length < 1" class="placeholder-container center-container active">
        <div class="center-content">
          <div class="placeholder-icon tiny"><svg width="24px" height="24px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-search"></use></svg></div>
          <h4 class="bold">Fant ingen treff</h4>
          <p class="no-margin">Valgt filter returnerte ingen treff.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import OppgaveService from '@/services/OppgaveService'
import TheLoader from '../../TheLoader.vue'
import bus from '@/plugins/event-bus'

export default {
  components: { TheLoader },
  name: 'TableOppgaver',
  props: {
    large: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    limit: {
      type: Number,
      required: true
    },
    statusId: {
      required: true
    },
    brukerId: {
      required: true
    }
  },
  data () {
    return {
      dashboarditem: {
        page: 1,
        pages: 1,
        total: 0,
        items: [],
        loading: true,
        updating: false,
        sort: 'createdAt',
        direction: 'desc',
        headings: [
          { name: 'Oppgave', sortKey: 'objekt.Objektnr', class: 'nowrap medium', skelTh: 'nowrap medium sk-text sk-text-narrow', skelTd: 'nowrap medium sk-text sk-text-wide' },
          { name: 'Status', sortKey: 'status', class: 'status-container', skelTh: 'status-container sk-text sk-text-narrow', skelTd: 'status-container sk-text sk-text-medium' },
          { name: 'Registrert', sortKey: 'createdAt', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Ansv.', sortKey: 'bruker.Fornavn', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-circle sk-circle-tiny' }
        ]
      }
    }
  },
  created () {
    bus.$off('oppgave-changed')
    this.dashboarditem.loading = true

    this.fetch()
  },
  mounted () {
    bus.$on('oppgave-changed', (data) => this.onOppgaveUpdated(data))
  },
  computed: {
    sortClassUnsigned: function () {
      return 'sorttable_sorted' + (this.dashboarditem.direction !== 'asc' ? '_reverse' : '')
    },
    skeletonThLines: function () {
      return this.dashboarditem.headings.map(o => o.skelTh)
    },
    skeletonTdLines: function () {
      const retval = []

      const tdvals = this.dashboarditem.headings.map(o => o.skelTd)

      for (let i = 0; i < this.limit; ++i) {
        retval.push(tdvals)
      }

      return retval
    }
  },
  watch: {
    statusId: function (newVal) {
      if (newVal !== undefined) {
        this.dashboarditem.page = 1
        this.dashboarditem.updating = true
        this.fetch()
      }
    },
    brukerId: function (newVal) {
      if (newVal !== undefined) {
        this.dashboarditem.page = 1
        this.dashboarditem.updating = true
        this.fetch()
      }
    }
  },
  methods: {
    onOppgaveUpdated: function (data) {
      let found = this.dashboarditem.items.findIndex(x => x.id === data.id)

      if (found !== -1) {
        this.dashboarditem.items.splice(found, 1, data)
      }
    },
    setPage: function (page) {
      const vm = this

      window.jQuery('html').animate({
        scrollTop: 0
      }, 200, function () {
        vm.dashboarditem.page = page
        vm.dashboarditem.updating = true
        vm.fetch()
      })
    },
    changeSorting: function (sortKey) {
      if (!sortKey) {
        return
      }

      if (this.dashboarditem.sort === sortKey) {
        this.dashboarditem.direction = this.dashboarditem.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.dashboarditem.direction = 'asc'
      }

      this.dashboarditem.sort = sortKey
      this.dashboarditem.page = 1
      this.dashboarditem.updating = true
      this.fetch()
    },
    fetch: function () {
      let queryParams = {
        page: this.dashboarditem.page,
        sort: this.dashboarditem.sort,
        direction: this.dashboarditem.direction,
        limit: this.limit,
        ansvarlig: this.brukerId,
        status: this.statusId
      }

      OppgaveService.getUnsoldObjects(queryParams)
        .then((response) => {
          this.dashboarditem.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.dashboarditem.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
          this.dashboarditem.items = response.data
        })
        .catch((error) => {
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste inn usolgte objekter')
          console.log(error)
        })
        .then(() => {
          this.dashboarditem.loading = false
          this.dashboarditem.updating = false

          this.$emit('tableLoaded', this.dashboarditem)
        })
    },
    objektThumb: function (item) {
      if (item.photos && item.photos.thumb && item.photos.thumb.length > 0) {
        return item.photos.thumb[0]
      }

      return null
    },
    objektThumbLoaded: function (item) {
      item.Fototatt = 'T'
    },
    // showObjektItem: function (item) {
    //   this.$router.push({
    //     name: 'KundeCard',
    //     params: { kunde_id: item.kontrakt.Kundenr },
    //     query: { kontrakt: item.Kontraktnr, objekt: item.Objektid },
    //     hash: ''
    //   })
    // }
    openUnsoldItemModal: function (item) {
      this.openModal('dashboard-oppgave-modal', { task: item, internalWindow: true }, 'fullsplit')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/Dashboard/TableOppgaver.vue